import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

// Material-UI
import Autocomplete from '@material-ui/lab/Autocomplete';
import Popper from '@material-ui/core/Popper';
import CircularProgress from '@material-ui/core/CircularProgress';

import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

import { Icons, MUI } from '@styles';

const MuiPopper = (props) => {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={{ computeStyle: { gpuAcceleration: false } }}
    />
  );
};

const MuiAutocomplete = (props) => {
  const {
    label,
    searchList,
    searchBy,
    value,
    error,
    helperText,
    onChange,
    ...restProps
  } = props;

  const isLoading = searchList.length === 0;

  const Loading = () => {
    return (
      <BoxLoading>
        <SpanText>
          {`${i18n.t('GenericMessages.loading')}...`}
        </SpanText>
        <BoxCircularSpinner>
          <CircularProgress
            color="inherit"
            size={20}
          />
        </BoxCircularSpinner>
      </BoxLoading>
    );
  };

  const NotFound = () => {
    return (
      <SpanText>
        {`${i18n.t('GenericMessages.noResults')}...`}
      </SpanText>
    );
  };

  return (
    <Autocomplete
      {...restProps}
      // debug={true}
      loading={isLoading}
      clearOnEscape={true}
      clearText={i18n.t('GenericMessages.clear')}
      closeText={i18n.t('DialogConfirm.close')}
      openText={i18n.t('GenericMessages.open')}
      loadingText={<Loading />}
      noOptionsText={<NotFound />}
      options={searchList}
      getOptionLabel={(option) => option[searchBy] || ''}
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      popupIcon={<Icons.ArrowDown />}
      PopperComponent={StyledPopper}
      renderInput={(params) => (
        <MUI.TextFieldIcon
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: <Icons.Search />,
          }}
        />
      )}
      renderOption={(option, { inputValue }) => {
        const matches = match(option[searchBy], inputValue);
        const parts = parse(option[searchBy], matches);

        return (
          <div>
            {parts.map((part, index) => (
              <SpanText
                key={index}
                style={{ fontWeight: part.highlight ? 700 : 400 }}
              >
                {part.text}
              </SpanText>
            ))}
          </div>
        );
      }}
    />
  );
};

MuiAutocomplete.propTypes = {
  // The label content.
  label: PropTypes.node,
  // Array of options.
  searchList: PropTypes.array.isRequired,
  // Used to determine the string value for a given option.
  searchBy: PropTypes.string,
  // The value of the autocomplete.
  value: PropTypes.oneOfType([PropTypes.any, PropTypes.array]),
  // The error bool.
  error: PropTypes.bool,
  // The helper text content.
  helperText: PropTypes.string,
  // Callback fired when the value changes.
  onChange: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const SpanText = styled.span`
    font-family: 'Bryant Pro Regular';
    font-size: ${(props) => props.theme.typography.bodyText};
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: ${(props) => props.theme.colors.greyDark};
`;

const BoxLoading = styled.div`
  display: flex;
  width: "100%";
  align-items: center;
`;

const BoxCircularSpinner = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
`;

// Popper 
const StyledPopper = styled(MuiPopper)`
  .MuiPaper-root {
    margin: 8px 0;
    box-shadow: ${(props) => props.theme.shadows.depth2};
  }

  .MuiAutocomplete-listbox {
    scrollbar-width: thin !important;
  }

  .MuiAutocomplete-option {
    padding: 8px 16px;
  }

  .MuiAutocomplete-option[aria-selected="true"] {
    background-color: ${(props) => props.theme.colors.greyLight};
  }
`;

// Autocomplete
export const StyledAutocomplete = styled(MuiAutocomplete)`
  .MuiAutocomplete-inputRoot .MuiAutocomplete-input {
    padding: 8px 0 !important;
  }
  
  .MuiAutocomplete-clearIndicator {
    visibility: ${(props) => (props.value === '' || props.value === null) ? 'hidden' : 'visible'};
  }
`;
